import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ScanResult } from '../types';
import { barcodeService } from '../services/payment/barcodeService';
import { qrService } from '../services/payment/qrService';

interface ScannerContextValue {
  scanning: boolean;
  lastResult: ScanResult | null;
  history: ScanResult[];
  scanQr: () => Promise<ScanResult | null>;
  scanBarcode: () => Promise<ScanResult | null>;
  clear: () => void;
}

const ScannerContext = createContext<ScannerContextValue | undefined>(undefined);

export function ScannerProvider({ children }: { children: ReactNode }) {
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState<ScanResult | null>(null);
  const [history, setHistory] = useState<ScanResult[]>([]);

  const run = useCallback(async (fn: () => ReturnType<typeof qrService.decode>) => {
    setScanning(true);
    const res = await fn();
    setScanning(false);
    if (res.ok) {
      setLastResult(res.data);
      setHistory((prev) => [res.data, ...prev]);
      return res.data;
    }
    return null;
  }, []);

  const scanQr = useCallback(() => run(() => qrService.decode()), [run]);
  const scanBarcode = useCallback(() => run(() => barcodeService.decode()), [run]);
  const clear = useCallback(() => setLastResult(null), []);

  const value = useMemo<ScannerContextValue>(
    () => ({ scanning, lastResult, history, scanQr, scanBarcode, clear }),
    [scanning, lastResult, history, scanQr, scanBarcode, clear],
  );

  return <ScannerContext.Provider value={value}>{children}</ScannerContext.Provider>;
}

export function useScannerContext() {
  const ctx = useContext(ScannerContext);
  if (!ctx) throw new Error('useScannerContext must be used within a ScannerProvider');
  return ctx;
}
